// Pure helpers for the call-out (reactive) job sheet, shared by the engineer PWA and
// the office view. A call-out records arrival/departure and the lift status on leaving (a
// fixed list). The lift-status list MIRRORS the server LIFT_STATUS_OPTIONS in
// server/src/modules/jobs/jobs.ts -- keep the two in step (the server validates against its
// own copy). Pure (no Date.now) so it unit tests directly.
//
// Session APF: the engineer's sheet no longer picks its times from the 15-minute dropdowns
// (timeDropdownOptions below is kept for any other caller). Every job type now captures them
// on the one Time on site card, and resolveVisitTimes at the foot of this file is the single
// place that decides what those fields, and any POP tag taps, become on the record.

// The exact options the engineer picks from for the lift status on leaving site
// (stored verbatim). Must match the server z.enum.
export const LIFT_STATUS_OPTIONS = [
  "Lift running normally",
  "Lift running with restrictions",
  "Lift taken out of service",
  "Awaiting parts",
  "Awaiting further instruction",
] as const;
export type LiftStatusOnLeaving = (typeof LIFT_STATUS_OPTIONS)[number];

// "HH:MM" time options in 15-minute steps. In hours: 06:00 to 22:00 inclusive. Out of
// hours: the full 24 hours (00:00 to 23:45). Pure.
export function timeDropdownOptions(outOfHours: boolean): string[] {
  const startMin = outOfHours ? 0 : 6 * 60;
  const endMin = outOfHours ? 24 * 60 - 15 : 22 * 60; // inclusive
  const out: string[] = [];
  for (let m = startMin; m <= endMin; m += 15) {
    const h = Math.floor(m / 60);
    const mm = m % 60;
    out.push(`${String(h).padStart(2, "0")}:${String(mm).padStart(2, "0")}`);
  }
  return out;
}

// The day after a "YYYY-MM-DD" date, as "YYYY-MM-DD". Computed in UTC so it never
// shifts a day at a timezone boundary. Pure.
export function nextDayYmd(dateYmd: string): string {
  const d = new Date(`${dateYmd}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return dateYmd;
  return new Date(d.getTime() + 24 * 60 * 60 * 1000).toISOString().slice(0, 10);
}

// Build an ISO datetime from a base date ("YYYY-MM-DD") and a "HH:MM" time, in the
// device's local timezone (so engineer and office, both UK, round-trip the same clock
// time). Returns null on a malformed input. Pure.
export function combineDateAndTime(dateYmd: string, hhmm: string): string | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(dateYmd) || !/^\d{2}:\d{2}$/.test(hhmm)) return null;
  const d = new Date(`${dateYmd}T${hhmm}:00`);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

// The local calendar date ("YYYY-MM-DD") of an ISO instant, in the device's timezone (engineer
// and office are both UK, so a stored instant round-trips to the same clock date). Empty string
// on a missing/malformed input so a date <input> can bind it directly. Pure.
export function isoToLocalYmd(iso: string | null | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

// The local wall-clock time ("HH:MM") of an ISO instant. Empty string on a missing/malformed
// input so a time <input> can bind it directly. Pure.
export function isoToLocalHHMM(iso: string | null | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

// True when a stored arrival/departure pair crosses a local midnight (an overnight out-of-hours
// call-out). Used when the office re-bases the times onto an edited visit date, so a genuine
// overnight span is preserved and anything else with departure <= arrival is rejected as a
// typo. Pure.
export function isoPairSpansMidnight(arrivalIso: string | null | undefined, departureIso: string | null | undefined): boolean {
  if (!arrivalIso || !departureIso) return false;
  const a = isoToLocalYmd(arrivalIso);
  const d = isoToLocalYmd(departureIso);
  return a !== "" && d !== "" && a !== d;
}

// Build the arrival/departure ISO pair for a visit on baseDate. A departure earlier
// than arrival only legitimately means the visit crossed midnight, which can ONLY
// happen out of hours -- so departure is pushed to the next day only when crossesMidnight
// (the OOH flag) is set AND the two times differ. An equal pair, or a reversed in-hours
// pair, is left same-day so the server (calloutLabourMinutes) rejects it as a data slip
// (no charge, no crash) rather than billing a spurious ~24h. Returns null if either time
// is unset/malformed. Pure.
export function buildArrivalDeparture(
  baseDateYmd: string,
  arrivalHHMM: string,
  departureHHMM: string,
  crossesMidnight = false,
): { arrivalISO: string; departureISO: string } | null {
  const arrivalISO = combineDateAndTime(baseDateYmd, arrivalHHMM);
  let departureISO = combineDateAndTime(baseDateYmd, departureHHMM);
  if (!arrivalISO || !departureISO) return null;
  const sameClock = arrivalHHMM === departureHHMM;
  if (crossesMidnight && !sameClock && new Date(departureISO).getTime() <= new Date(arrivalISO).getTime()) {
    departureISO = combineDateAndTime(nextDayYmd(baseDateYmd), departureHHMM) ?? departureISO;
  }
  return { arrivalISO, departureISO };
}

// ---- What a visit's arrival and departure ARE, decided in one place (Session APF) ---------------
//
// The engineer's sheet has ONE pair of time fields for every job type (the Time on site card:
// Arrived and Left, each typed, or set with its Now button). Since Session APF a POP tag tap made
// from the sheet PRE-FILLS the matching field. This function turns those two fields, plus any tap
// stamps behind them, into the two instants the completion payload carries. It is also what the
// sheet's confirm panel reads, so the engineer is shown exactly what is about to become the record.
//
// Pure: `visitYmd` and `nowIso` are handed in, never read from the clock in here.
export interface VisitTimesInput {
  // The day of the visit, "YYYY-MM-DD". A typed "HH:MM" carries no date of its own, so this is the
  // day it is taken to belong to. Session BAR: it is the VISIT DATE the engineer chose on the sheet
  // (today unless they picked an earlier day), no longer always the device's date at submission, so
  // a sheet finished the morning after the visit records the visit on the day it happened.
  visitYmd: string;
  // The moment of submission, ISO.
  nowIso: string;
  // When the sheet was first opened for this piece of work, ISO, or null if that is not known.
  openedAtIso: string | null;
  // The two fields as they stand, "HH:MM" or "" when empty.
  arrivalHHMM: string;
  departureHHMM: string;
  // The POP tag tap behind each field, ISO, or null when that end of the visit was not tapped.
  arrivalStampIso: string | null;
  departureStampIso: string | null;
  // TRUE for a service or observational visit, FALSE for a call-out, a repair or a tech visit. See
  // the two rules in the function below: this is the difference between them.
  fallBackToOpenAndNow: boolean;
  // The out-of-hours flag on a call-out-style sheet. Only an out-of-hours visit can legitimately
  // cross midnight (the rule buildArrivalDeparture above already applies).
  crossesMidnight: boolean;
  // Session BAR: FALSE when the visit was on an EARLIER DAY. "When the sheet was opened" and "now"
  // are both today, so neither can stand in for a time on another day: with this off, a blank field
  // stays blank (the sheet asks for the times instead). Absent means true, exactly as before.
  allowNowDefaults?: boolean;
}

export interface ResolvedVisitTimes {
  arrivalISO: string | null;
  departureISO: string | null;
}

// One field, resolved. `fromStamp` says whether the answer is the tap's own instant (which carries
// its real date) or a typed clock time put on today's date.
//
// THE STAMP WINS ONLY WHILE THE FIELD STILL SHOWS IT. A tap pre-fills the field with its own local
// "HH:MM"; if the field still reads that, the engineer has accepted the tap and the visit is
// recorded at the tap's EXACT instant, seconds and date included, which is what makes a tap either
// side of midnight come out right with no guesswork. The moment the engineer changes the field, what
// they typed is the record and the stamp is ignored: a pre-fill is a default, never an override.
// The instant is re-serialised rather than passed through as the string it arrived as. For the
// canonical form the server sends, and the phone's own toISOString(), that changes nothing; for
// anything else it guarantees the payload still carries the UTC "Z" form the completion route
// validates, so an oddly formatted stamp can never turn a finished sheet into one that cannot sync.
function resolveTimeField(visitYmd: string, hhmm: string, stampIso: string | null): { iso: string; fromStamp: boolean } | null {
  if (!hhmm) return null;
  if (stampIso && isoToLocalHHMM(stampIso) === hhmm) return { iso: new Date(stampIso).toISOString(), fromStamp: true };
  const iso = combineDateAndTime(visitYmd, hhmm);
  return iso ? { iso, fromStamp: false } : null;
}

export function resolveVisitTimes(input: VisitTimesInput): ResolvedVisitTimes {
  const arrival = resolveTimeField(input.visitYmd, input.arrivalHHMM, input.arrivalStampIso);
  const departure = resolveTimeField(input.visitYmd, input.departureHHMM, input.departureStampIso);

  // An out-of-hours visit whose TYPED times run backwards crossed midnight, so the departure
  // belongs to the next day: the one rule buildArrivalDeparture has always applied, reused rather
  // than restated. It is for typed times only. A stamp is a whole instant with its own date, so a
  // pair involving one needs no such inference and must not be given one.
  if (input.crossesMidnight && arrival && departure && !arrival.fromStamp && !departure.fromStamp) {
    const pair = buildArrivalDeparture(input.visitYmd, input.arrivalHHMM, input.departureHHMM, true);
    if (pair) return pair;
  }

  // Session BAR: a visit on an earlier day takes no default from today's clock, for any job type.
  // What was entered is recorded; nothing is invented (and a call-out with no arrival still records
  // no times at all, the locked call-out SLA clock rule).
  if (input.allowNowDefaults === false) {
    if (!arrival && !input.fallBackToOpenAndNow) return { arrivalISO: null, departureISO: null };
    return { arrivalISO: arrival?.iso ?? null, departureISO: departure?.iso ?? null };
  }

  if (input.fallBackToOpenAndNow) {
    // A SERVICE OR OBSERVATIONAL VISIT: exactly what the sheet has done since Session ADJ. A blank
    // Arrived falls back to when the sheet was opened and a blank Left to the moment of submission,
    // so the service record always shows a duration. Nothing downstream is judged on these.
    return {
      arrivalISO: arrival?.iso ?? (input.openedAtIso || null),
      departureISO: departure?.iso ?? input.nowIso,
    };
  }

  // A CALL-OUT, A REPAIR OR A TECH VISIT: the arrival is recorded ONLY when the field holds a value
  // (typed, set with Now, or pre-filled by a tap). IT NEVER FALLS BACK TO WHEN THE SHEET WAS OPENED.
  // A call-out's arrival feeds CallOut.attendedAt, the attendance stop of the SLA clock, and the
  // locked CALL-OUT SLA CLOCK RULE (CLAUDE.md) is that a missing stop is UNMEASURED, never invented.
  // Engineers are known to submit their sheets hours after the visit, so "when the sheet was
  // opened" would be a late arrival nobody made: a fabricated breach of a promise that was kept.
  //
  // And with no arrival there are no times at all, even if Left holds a value. That keeps the two
  // outcomes this sheet has always had for these jobs (both times, or neither), so nothing
  // downstream meets a departure with no arrival, a shape it has never been given. The confirm
  // panel tells the engineer so before they submit, and how to put it right.
  if (!arrival) return { arrivalISO: null, departureISO: null };

  // A blank Left defaults to the moment of submission, but only now that there is an arrival for
  // it to be the end of.
  return { arrivalISO: arrival.iso, departureISO: departure?.iso ?? input.nowIso };
}

// ---- Session BAR: the visit date, and typing a time without the phone's clock dial ---------------

// The device's local calendar date of an instant, "YYYY-MM-DD". Pure.
export function localYmd(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

// A "YYYY-MM-DD" date moved by whole days, computed in UTC so it never slips at a clock change. Pure.
export function addDaysYmd(dateYmd: string, days: number): string {
  const d = new Date(`${dateYmd}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return dateYmd;
  return new Date(d.getTime() + days * 24 * 60 * 60 * 1000).toISOString().slice(0, 10);
}

// The visit date a sheet opens on: the day the job was booked for when that is today or earlier (the
// commonest late close is yesterday's job finished this morning), otherwise today. A job with no
// booked day, or one booked for a later day, opens on today. Pure.
export function defaultVisitYmd(scheduledForIso: string | null | undefined, now: Date): string {
  const today = localYmd(now);
  const booked = isoToLocalYmd(scheduledForIso ?? null);
  return booked && booked <= today ? booked : today;
}

// "Tue 6 Oct", the way the sheet and its confirm panel name a visit day. Pure (UTC maths on the date
// string, so it never shifts a day).
export function visitDayLabel(dateYmd: string): string {
  const d = new Date(`${dateYmd}T12:00:00Z`);
  if (Number.isNaN(d.getTime())) return dateYmd;
  const days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  return `${days[d.getUTCDay()]} ${d.getUTCDate()} ${months[d.getUTCMonth()]}`;
}

// What Job.completedAt carries for a visit on an EARLIER day. It is not the submission instant: it
// is the date of the visit, which the office approval stamps as the lift's last service (the PPM
// anchor) and the service record prints. So it is the recorded departure when there is one, else
// midday on the visit day (a neutral instant whose date is the visit day in every UK offset), and
// never later than now. Pure.
export function pastVisitCompletedAt(visitYmd: string, departureIso: string | null, nowIso: string): string {
  const now = new Date(nowIso).getTime();
  const candidate = departureIso ?? combineDateAndTime(visitYmd, "12:00") ?? nowIso;
  return new Date(Math.min(new Date(candidate).getTime(), now)).toISOString();
}

// Typed digits made into a 24-hour "HH:MM", or null while the entry is not yet a whole valid time.
// Accepts what a numeric keypad produces: "930" or "0930" is 09:30, "1745" is 17:45, "9" is 09:00,
// "17" is 17:00, and a value already written "HH:MM" (or "H:MM") is kept. No AM or PM, no dial. Pure.
export function parseTypedTime(raw: string): string | null {
  const s = raw.trim();
  if (!s) return null;
  let h: number;
  let m: number;
  const colon = /^(\d{1,2})[:.](\d{2})$/.exec(s);
  if (colon) {
    h = Number(colon[1]);
    m = Number(colon[2]);
  } else if (/^\d{1,4}$/.test(s)) {
    if (s.length <= 2) {
      h = Number(s);
      m = 0;
    } else {
      h = Number(s.slice(0, s.length - 2));
      m = Number(s.slice(-2));
    }
  } else {
    return null;
  }
  if (h > 23 || m > 59) return null;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}

// A "HH:MM" moved by a number of minutes, wrapping round the 24-hour clock. Pure.
export function shiftHHMM(hhmm: string, deltaMins: number): string {
  const match = /^(\d{2}):(\d{2})$/.exec(hhmm);
  if (!match) return hhmm;
  const total = (((Number(match[1]) * 60 + Number(match[2]) + deltaMins) % 1440) + 1440) % 1440;
  return `${String(Math.floor(total / 60)).padStart(2, "0")}:${String(total % 60).padStart(2, "0")}`;
}
// Whole calendar days from the local date of one instant to the local date of a later one (0 when
// either is missing or the later is not later). The office reads it as "sent in N days after the
// visit". Pure.
export function daysBetweenLocalDates(fromIso: string | null | undefined, toIso: string | null | undefined): number {
  const a = isoToLocalYmd(fromIso);
  const b = isoToLocalYmd(toIso);
  if (!a || !b || b <= a) return 0;
  return Math.round((Date.parse(`${b}T00:00:00Z`) - Date.parse(`${a}T00:00:00Z`)) / (24 * 60 * 60 * 1000));
}